import { KeyboardEvent, RefObject, useEffect, useRef, useState } from 'react';
import {
  buildFragmentFromClipboardText,
  caretLogicalOffset,
  setCaretLogicalOffset,
} from './clipboard';
import { CHIP_SELECTED_CLASS, isMentionChipElement } from './dom-chip';
import {
  isEditorEmpty,
  SerializedEditorContent,
  serializeEditor,
} from './serialize';
import {
  isUnresolvedMentionElement,
  syncUnresolvedMentions,
} from './unresolved-mention';

export interface UseMentionEditorArgs {
  autoFocus?: boolean;
  onChange?: (content: SerializedEditorContent) => void;
  onSubmit?: () => void;
  onAfterRecompute?: (root: HTMLElement) => void;
  onBeforeCaretRestore?: (root: HTMLElement) => void;
}

export interface UseMentionEditorResult {
  editorRef: RefObject<HTMLDivElement | null>;
  isEmpty: boolean;
  emitChange: () => void;
  recompute: () => void;
  clearChipSelection: () => void;
  insertLineBreak: () => void;
  handleChipBackspace: () => boolean;
  handleEnterKey: (e: KeyboardEvent<HTMLDivElement>) => boolean;
  clearEditorDom: () => void;
  setEditorText: (text: string) => void;
}

export const useMentionEditor = ({
  autoFocus,
  onChange,
  onSubmit,
  onAfterRecompute,
  onBeforeCaretRestore,
}: UseMentionEditorArgs): UseMentionEditorResult => {
  const editorRef = useRef<HTMLDivElement>(null);
  const onAfterRecomputeRef = useRef(onAfterRecompute);
  onAfterRecomputeRef.current = onAfterRecompute;
  const [isEmpty, setIsEmpty] = useState(true);

  useEffect(() => {
    if (autoFocus) {
      editorRef.current?.focus();
    }
  }, [autoFocus]);

  const emitChange = () => {
    const root = editorRef.current;
    if (!root) {
      return;
    }
    setIsEmpty(isEditorEmpty(root));
    onChange?.(serializeEditor(root));
  };

  const recompute = () => {
    const root = editorRef.current;
    if (!root) {
      return;
    }

    const selection = window.getSelection();
    let logicalOffset: number | null = null;
    if (
      selection &&
      selection.rangeCount > 0 &&
      selection.isCollapsed &&
      selection.anchorNode &&
      root.contains(selection.anchorNode)
    ) {
      logicalOffset = caretLogicalOffset(
        root,
        selection.anchorNode,
        selection.anchorOffset
      );
    }

    onBeforeCaretRestore?.(root);

    if (logicalOffset !== null) {
      setCaretLogicalOffset(root, logicalOffset);
    }

    emitChange();
    onAfterRecomputeRef.current?.(root);
  };

  const clearChipSelection = () => {
    editorRef.current
      ?.querySelectorAll(`.${CHIP_SELECTED_CLASS}`)
      .forEach(el => el.classList.remove(CHIP_SELECTED_CLASS));
  };

  const insertLineBreak = () => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return;
    }
    const range = selection.getRangeAt(0);
    range.deleteContents();
    const br = document.createElement('br');
    range.insertNode(br);
    range.setStartAfter(br);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
    emitChange();
  };

  const removeSelectedAtomic = (
    root: HTMLElement,
    selected: Element,
    trimFollowingNbsp: boolean
  ) => {
    const next = selected.nextSibling;
    selected.remove();
    if (
      trimFollowingNbsp &&
      next?.nodeType === Node.TEXT_NODE &&
      next.nodeValue?.startsWith('\u00a0')
    ) {
      next.nodeValue = next.nodeValue.slice(1);
      if (next.nodeValue.length === 0) {
        next.remove();
      }
    }
    recompute();
  };

  const findAtomicCandidateBeforeCaret = (range: Range): ChildNode | null => {
    const node = range.startContainer;
    const offsetInNode = range.startOffset;
    if (node.nodeType !== Node.TEXT_NODE) {
      return null;
    }
    if (offsetInNode === 0) {
      return node.previousSibling;
    }
    if (offsetInNode === 1 && node.nodeValue?.[0] === '\u00a0') {
      return node.previousSibling;
    }
    return null;
  };

  const handleChipBackspace = (): boolean => {
    const root = editorRef.current;
    const selection = window.getSelection();
    if (
      !root ||
      !selection ||
      selection.rangeCount === 0 ||
      !selection.isCollapsed
    ) {
      return false;
    }

    const selected = root.querySelector(`.${CHIP_SELECTED_CLASS}`);
    if (selected) {
      removeSelectedAtomic(root, selected, isMentionChipElement(selected));
      return true;
    }

    const candidate = findAtomicCandidateBeforeCaret(selection.getRangeAt(0));
    if (
      isMentionChipElement(candidate) ||
      isUnresolvedMentionElement(candidate)
    ) {
      candidate.classList.add(CHIP_SELECTED_CLASS);
      return true;
    }

    return false;
  };

  const handleEnterKey = (e: KeyboardEvent<HTMLDivElement>): boolean => {
    if (e.key !== 'Enter' || e.nativeEvent.isComposing) {
      return false;
    }
    if (e.shiftKey) {
      insertLineBreak();
    } else {
      onSubmit?.();
    }
    return true;
  };

  const clearEditorDom = () => {
    if (editorRef.current) {
      editorRef.current.innerHTML = '';
    }
    emitChange();
  };

  const setEditorText = (text: string) => {
    const root = editorRef.current;
    if (root) {
      const { fragment } = buildFragmentFromClipboardText(text, 0);
      root.replaceChildren(fragment);
      syncUnresolvedMentions(root);
    }
    emitChange();
  };

  return {
    editorRef,
    isEmpty,
    emitChange,
    recompute,
    clearChipSelection,
    insertLineBreak,
    handleChipBackspace,
    handleEnterKey,
    clearEditorDom,
    setEditorText,
  };
};
